/**
 * QueueSegmentTable — renders the column set for the active segment, with rows
 * GROUPED into sections by their routing rule (Default first, then rule-based).
 * The header + cells are driven by QUEUE_COLUMNS[segment], so each state
 * (queued / testing / removed) shows only its meaningful columns.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';
import { ChevronRight, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import type { QueueElement } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { type QueueSegment } from '../queue-filters';
import { QUEUE_COLUMNS, type QueueCellCtx, type QueueColumn } from './queue-columns';
import { RuleBadge } from './queue-cells';
import { groupByRule, type QueueRuleSection } from './queue-rule-groups';
import { useRuleCollapse } from './use-rule-collapse';
import {
  QueueHeaderRow,
  defaultSortFor,
  sortQueueRows,
  toggleQueueSort,
} from './sort';

export interface QueueSegmentTableProps {
  segment: QueueSegment;
  elements: QueueElement[];
  ctx: QueueCellCtx;
}

const rowKey = (el: QueueElement, i: number): string =>
  `${el.layer}:${el.poolId}:${el.bankId}:${i}`;

const DataRow: FC<{ el: QueueElement; columns: QueueColumn[]; ctx: QueueCellCtx }> = ({
  el,
  columns,
  ctx,
}) => {
  const isNext = ctx.nextUp.has(el.bankId);
  return (
    <tr
      title={isNext ? 'Next up for rotation' : undefined}
      className={cn(
        'border-t border-border/60 transition-colors',
        isNext ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/40',
      )}
    >
      {columns.map((c, ci) => (
        <td
          key={c.key}
          className={cn(
            'px-2 py-1.5 align-middle text-sm',
            c.align === 'right' && 'text-right',
            ci === 0 && isNext && 'border-l-2 border-l-primary',
          )}
        >
          {c.cell(el, ctx)}
        </td>
      ))}
    </tr>
  );
};

const SectionHeaderRow: FC<{
  section: QueueRuleSection;
  colSpan: number;
  open?: boolean;
  onToggle?: () => void;
}> = ({ section, colSpan, open = true, onToggle }) => (
  <tr className="bg-muted/30">
    <td colSpan={colSpan} className="px-2 py-1">
      {/* The whole header is the hit target — a 14px chevron is a poor one. */}
      <button
        type="button"
        onClick={onToggle}
        disabled={!onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-left enabled:hover:bg-muted disabled:cursor-default"
      >
        {onToggle ? (
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
              open && 'rotate-90',
            )}
            aria-hidden
          />
        ) : null}
        <RuleBadge rule={section.rule ?? undefined} />
        <span className="text-[11px] tabular-nums text-muted-foreground">
          · {section.elements.length}
        </span>
      </button>
    </td>
  </tr>
);

/**
 * Waiting is read as ONE list, not per rule.
 *
 * Sorting happens before grouping, so with sections the newest row overall can
 * sit below an older one that happens to head another rule's section — which
 * makes "newest first" a lie on the only axis this segment is read by. The
 * other segments group because their question is per-rule ("what is this rule
 * running / what did it kill"); Waiting's is "what landed here recently".
 */
const GROUPED_SEGMENTS: ReadonlySet<QueueSegment> = new Set<QueueSegment>([
  'queued',
  'testing',
  'removed',
]);

const QueueSegmentTable: FC<QueueSegmentTableProps> = ({ segment, elements, ctx }) => {
  const columns = QUEUE_COLUMNS[segment];
  // QueueTab renders a different branch per segment, so this remounts — and
  // therefore resets to the segment's default — when you switch tabs or layer.
  const [sort, setSort] = useState(() => defaultSortFor(segment));
  // Sorted BEFORE grouping: groupByRule is a stable partition, so ordered input
  // gives ordered sections without it needing to know about sorting at all.
  const sections = useMemo(
    () =>
      GROUPED_SEGMENTS.has(segment)
        ? groupByRule(sortQueueRows(elements, sort, columns))
        : [{ key: 'all', rule: null, elements: sortQueueRows(elements, sort, columns) }],
    [elements, sort, columns, segment],
  );
  // Skip a lone "No rule" header (old/ungrouped data) — but always label a real rule.
  const showHeaders =
    GROUPED_SEGMENTS.has(segment) &&
    (sections.length > 1 || Boolean(sections[0]?.rule));
  const collapse = useRuleCollapse(sections.map((s) => s.key));
  // Only when the headers are shown AND there is more than one to fold.
  const canCollapse = showHeaders && collapse.worthShowing;
  const CollapseIcon = collapse.allCollapsed ? ChevronsUpDown : ChevronsDownUp;
  return (
    <div className="space-y-1">
      {canCollapse ? (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={collapse.toggleAll}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CollapseIcon className="h-3.5 w-3.5" aria-hidden />
            {collapse.allCollapsed ? 'Expand all rules' : 'Collapse all rules'}
          </button>
        </div>
      ) : null}
      <div className="overflow-x-auto rounded-md border border-border/60">
      <table className="w-full text-left">
        <QueueHeaderRow
          columns={columns}
          sort={sort}
          onSort={(col) => setSort((cur) => toggleQueueSort(cur, col))}
        />
        {sections.map((section) => {
          const open = !canCollapse || collapse.isOpen(section.key);
          return (
            <tbody key={section.key}>
              {showHeaders && (
                <SectionHeaderRow
                  section={section}
                  colSpan={columns.length}
                  open={open}
                  onToggle={canCollapse ? () => collapse.toggleOne(section.key) : undefined}
                />
              )}
              {open
                ? section.elements.map((el, i) => (
                    <DataRow key={rowKey(el, i)} el={el} columns={columns} ctx={ctx} />
                  ))
                : null}
            </tbody>
          );
        })}
      </table>
      </div>
    </div>
  );
};

export default QueueSegmentTable;
